/**
 * GST engine. Rates are NEVER hardcoded — every percentage comes from the TaxRate table
 * (stored as basis points) and the active tax is chosen by the product's GST rate id.
 *
 * Supports intra-state (CGST + SGST) and inter-state (IGST) supply, plus cess.
 */

import type { BasisPoints, Paise } from './money.js';
import { applyRatio, roundHalfUp } from './money.js';

export interface TaxRate {
  id: string;
  name: string;
  /** 500 = 5% */
  gstBps: BasisPoints;
  /** 150 = 1.5% (health / luxury cess) */
  cessBps: BasisPoints;
  description?: string | null;
  isActive: boolean;
}

export interface TaxComponents {
  taxablePaise: Paise;
  cgstPaise: Paise;
  sgstPaise: Paise;
  igstPaise: Paise;
  cessPaise: Paise;
  gstPaise: Paise;
  totalPaise: Paise;
}

export const ZERO_COMPONENTS: TaxComponents = {
  taxablePaise: 0,
  cgstPaise: 0,
  sgstPaise: 0,
  igstPaise: 0,
  cessPaise: 0,
  gstPaise: 0,
  totalPaise: 0,
};

export const RATE_DENOMINATOR = 10_000;

export function zeroRate(): TaxRate {
  return { id: 'zero', name: 'GST 0%', gstBps: 0, cessBps: 0, isActive: true };
}

/**
 * Tax on an EXCLUSIVE price (tax added on top of the amount shown to the customer).
 * `interState` selects IGST instead of CGST+SGST.
 */
export function computeTaxExclusive(
  taxablePaise: Paise,
  rate: TaxRate,
  interState: boolean,
): TaxComponents {
  const gstPaise = applyRatio(taxablePaise, rate.gstBps, RATE_DENOMINATOR);
  const cessPaise = applyRatio(taxablePaise, rate.cessBps, RATE_DENOMINATOR);

  let cgstPaise = 0;
  let sgstPaise = 0;
  let igstPaise = 0;
  if (gstPaise > 0) {
    if (interState) {
      igstPaise = gstPaise;
    } else {
      // Split so that cgst + sgst always equals gst exactly (no lost paise).
      cgstPaise = roundHalfUp(gstPaise / 2);
      sgstPaise = gstPaise - cgstPaise;
    }
  }

  const totalPaise = taxablePaise + gstPaise + cessPaise;
  return { taxablePaise, cgstPaise, sgstPaise, igstPaise, cessPaise, gstPaise, totalPaise };
}

/**
 * Tax already EMBEDDED in the price (e.g. a shelf price printed "inclusive of all taxes").
 * Taxes are derived from the gross, then the taxable value is the residual, so a printed
 * receipt always adds up to the rupee without an off-by-one-paise.
 */
export function computeTaxInclusive(grossPaise: Paise, rate: TaxRate, interState: boolean): TaxComponents {
  const denominator = RATE_DENOMINATOR + rate.gstBps + rate.cessBps;
  // Single-step extraction from the gross keeps every component on the same base,
  // then the residual goes to taxable so taxable+gst+cess always equals the gross.
  const taxablePaise = applyRatio(grossPaise, RATE_DENOMINATOR, denominator);
  const gstPaise = applyRatio(grossPaise, rate.gstBps, denominator);
  const cessPaise = applyRatio(grossPaise, rate.cessBps, denominator);
  const drift = grossPaise - (taxablePaise + gstPaise + cessPaise);
  const adjustedTaxable = taxablePaise + drift;

  let cgstPaise = 0;
  let sgstPaise = 0;
  let igstPaise = 0;
  if (gstPaise > 0) {
    if (interState) {
      igstPaise = gstPaise;
    } else {
      cgstPaise = roundHalfUp(gstPaise / 2);
      sgstPaise = gstPaise - cgstPaise;
    }
  }

  const taxableFinal = adjustedTaxable;
  const totalPaise = taxableFinal + gstPaise + cessPaise;
  return { taxablePaise: taxableFinal, cgstPaise, sgstPaise, igstPaise, cessPaise, gstPaise, totalPaise };
}

export function computeTax(
  amountPaise: Paise,
  rate: TaxRate,
  options: { inclusive: boolean; interState: boolean },
): TaxComponents {
  return options.inclusive
    ? computeTaxInclusive(amountPaise, rate, options.interState)
    : computeTaxExclusive(amountPaise, rate, options.interState);
}

export function addComponents(a: TaxComponents, b: TaxComponents): TaxComponents {
  return {
    taxablePaise: a.taxablePaise + b.taxablePaise,
    cgstPaise: a.cgstPaise + b.cgstPaise,
    sgstPaise: a.sgstPaise + b.sgstPaise,
    igstPaise: a.igstPaise + b.igstPaise,
    cessPaise: a.cessPaise + b.cessPaise,
    gstPaise: a.gstPaise + b.gstPaise,
    totalPaise: a.totalPaise + b.totalPaise,
  };
}

/** One row of the GST summary report / receipt HSN breakup. */
export interface TaxBucket {
  rateId: string;
  rateName: string;
  gstBps: BasisPoints;
  cessBps: BasisPoints;
  taxablePaise: Paise;
  cgstPaise: Paise;
  sgstPaise: Paise;
  igstPaise: Paise;
  cessPaise: Paise;
  totalPaise: Paise;
}

export function emptyBucket(rate: TaxRate): TaxBucket {
  return {
    rateId: rate.id,
    rateName: rate.name,
    gstBps: rate.gstBps,
    cessBps: rate.cessBps,
    taxablePaise: 0,
    cgstPaise: 0,
    sgstPaise: 0,
    igstPaise: 0,
    cessPaise: 0,
    totalPaise: 0,
  };
}

export function mergeBucket(bucket: TaxBucket, components: TaxComponents): TaxBucket {
  return {
    ...bucket,
    taxablePaise: bucket.taxablePaise + components.taxablePaise,
    cgstPaise: bucket.cgstPaise + components.cgstPaise,
    sgstPaise: bucket.sgstPaise + components.sgstPaise,
    igstPaise: bucket.igstPaise + components.igstPaise,
    cessPaise: bucket.cessPaise + components.cessPaise,
    totalPaise: bucket.totalPaise + components.totalPaise,
  };
}

export function bucketsToRows(buckets: Map<string, TaxBucket>): TaxBucket[] {
  return [...buckets.values()]
    .filter((bucket) => bucket.taxablePaise !== 0 || bucket.totalPaise !== 0)
    .sort((a, b) => a.gstBps - b.gstBps);
}

/** Missing store or customer state defaults to intra-state (false) so a misconfigured
 *  store never silently charges IGST. Callers that need a loud failure must check the
 *  store state themselves before pricing (the sale service does). */
export function isInterStateSupply(input: {
  storeStateCode: string | null | undefined;
  customerStateCode: string | null | undefined;
}): boolean {
  const store = (input.storeStateCode ?? '').trim();
  const customer = (input.customerStateCode ?? '').trim();
  if (!store || !customer) return false;
  return store.toUpperCase() !== customer.toUpperCase();
}

const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

/** Format check only (no GST check-digit validation): a well-formed but mistyped GSTIN passes. */
export function isValidGstin(gstin: string | null | undefined): boolean {
  if (!gstin) return false;
  return GSTIN_PATTERN.test(gstin.trim().toUpperCase());
}

/** Extract the 2-digit state code embedded in the first two characters of a GSTIN. */
export function gstinStateCode(gstin: string | null | undefined): string | null {
  if (!isValidGstin(gstin)) return null;
  return gstin!.trim().slice(0, 2).toUpperCase();
}

/** HSN breakup rows for the receipt. Every bucket is a row, including 0% / exempt. */
export function hsnSummary(buckets: TaxBucket[]): TaxBucket[] {
  return [...buckets];
}
